import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Typography,
  IconButton,
  Grid,
  Chip,
  Container,
  Paper,
  LinearProgress,
  Alert,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SettingsIcon from "@mui/icons-material/Settings";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TvIcon from "@mui/icons-material/Tv";
import KitchenIcon from "@mui/icons-material/Kitchen";
import ComputerIcon from "@mui/icons-material/Computer";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import LocalLaundryServiceIcon from "@mui/icons-material/LocalLaundryService";
import MicrowaveIcon from "@mui/icons-material/Microwave";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import RouterIcon from "@mui/icons-material/Router";
import SpeakerIcon from "@mui/icons-material/Speaker";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import StarIcon from "@mui/icons-material/Star";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Recommendation from "./recommendation";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";


const postToWebhook = async (userData) => {
  try {
    const webhookUrl = "https://n8n.kverty.com/webhook/wattwise-save-entry"; 

    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData) 
    });

    if (response.ok) {
      console.log("User data successfully posted to webhook");
    } else {
      console.error("Webhook post failed:", response.statusText);
    }
  } catch (error) {
    console.error("Error posting to webhook:", error);
  }
};

const steps = [
  "Personal Details & Appliances",
  "Backup Duration",
  "Budget Range",
  "Inverter Type",
];

const applianceOptions = [
  { name: "Fan", watt: 75, icon: <AcUnitIcon />, color: "rgba(0, 0, 0, 0.79)" }, 
  { name: "Light (LED Bulb)", watt: 9, icon: <LightbulbIcon />, color: "rgba(0, 0, 0, 0.79)" },
  { name: "TV", watt: 100, icon: <TvIcon />, color: "rgba(0, 0, 0, 0.79)" }, 
  { name: "Refrigerator", watt: 250, icon: <KitchenIcon />, color: "rgba(0, 0, 0, 0.79)" }, 
  { name: "Computer", watt: 150, icon: <ComputerIcon />, color: "rgba(0, 0, 0, 0.79)" }, 
  { name: "Air Conditioner", watt: 1500, icon: <AcUnitIcon />, color: "rgba(0, 0, 0, 0.79)" }, 
  { name: "Washing Machine", watt: 500, icon: <LocalLaundryServiceIcon />, color: "rgba(0, 0, 0, 0.79)" }, 
  { name: "Microwave", watt: 1200, icon: <MicrowaveIcon />, color: "rgba(0, 0, 0, 0.79)" }, 
  { name: "Water Heater (Geyser)", watt: 2000, icon: <WaterDropIcon />, color: "rgba(0, 0, 0, 0.79)" },
  { name: "Music System", watt: 150, icon: <SpeakerIcon />, color: "rgba(0, 0, 0, 0.79)" }, 
];

const GREEN = "rgba(9, 135, 66, 0.79)";
const WHITE = "#fff";
const LIGHT_NAVY_BG = "#f5f8fc";

const SERVER_STATUS_URL = "https://n8n.kverty.com/webhook/fetch-inverters"; // Use any endpoint that should be live

const Inverter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [activeStep, setActiveStep] = useState(0);
  const [user, setUser] = useState({ name: "", phone: "" });
  const [appliances, setAppliances] = useState([]);
  const [backupTime, setBackupTime] = useState(4);
  const [budget, setBudget] = useState(15000);
  const [inverterType, setInverterType] = useState("pure_sine");
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [serverStatus, setServerStatus] = useState("checking"); 

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100..900;1,100..900&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    document.body.style.fontFamily = '"Noto Sans", sans-serif';

    return () => {
      document.head.removeChild(link);
      document.body.style.fontFamily = "";
    };
  }, []);

  // Server status check effect
  useEffect(() => {
    let isMounted = true;
    setServerStatus("checking");
    fetch(SERVER_STATUS_URL, { method: "GET" })
      .then((res) => {
        if (isMounted) setServerStatus(res.ok ? "live" : "down");
      })
      .catch(() => {
        if (isMounted) setServerStatus("down");
      });
    return () => { isMounted = false; };
  }, []);

  const totalLoad = appliances.reduce((acc, item) => acc + item.watt, 0);
  const progress = ((activeStep + 1) / steps.length) * 100;

  const handleAddAppliance = (name, watt, icon, color) => {
    setAppliances([...appliances, { name, watt, icon, color }]);
  };

  const handleRemoveAppliance = (index) => {
    const newAppliances = [...appliances];
    newAppliances.splice(index, 1);
    setAppliances(newAppliances);
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/\D/g, "");
    return /^[6-9]\d{9}$/.test(cleanPhone);
  };

  const formatPhoneNumber = (phone) => {
    const cleanPhone = phone.replace(/\D/g, "");
    return cleanPhone.length <= 10 ? cleanPhone : cleanPhone.slice(0, 10);
  };

  const handleNext = () => {
    if (serverStatus === "down") {
      alert("Server is currently down. Please try again later.");
      return;
    }
    if (activeStep === steps.length - 1) {
      const userData = {
        ...user,
        appliances: appliances.map((a) => ({ name: a.name, watt: a.watt })),
        totalLoad,
        backupTime,
        budget,
        inverterType,
        timestamp: new Date().toISOString(),
      };
      
      // Post to webhook and show recommendation
      postToWebhook(userData);
      setShowRecommendation(true);
    } else {
      if (activeStep === 0) {
        if (!user.name || !user.phone) {
          alert("Please enter Name and Mobile Number.");
          return;
        }
        if (!validatePhone(user.phone)) {
          alert("Please enter a valid 10-digit mobile number.");
          return;
        }
        if (appliances.length === 0) {
          alert("Please select at least one appliance.");
          return;
        }
      }
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const getStepIcon = (step) => {
    switch (step) {
      case 0:
        return <PersonIcon />;
      case 1:
        return <AccessTimeIcon />;
      case 2:
        return <AccountBalanceWalletIcon />;
      case 3:
        return <SettingsIcon />;
      default:
        return <FlashOnIcon />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: LIGHT_NAVY_BG,
        py: isMobile ? 1 : isTablet ? 2 : 3,
        fontFamily: '"Noto Sans", sans-serif',
        px: isMobile ? 1 : 0,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          py: isMobile ? 1 : isTablet ? 2 : 3,
          px: isMobile ? 1 : 0,
        }}
      >
        {/* Status LED */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <FiberManualRecordIcon
            sx={{
              color:
                serverStatus === "live"
                  ? GREEN
                  : serverStatus === "down"
                  ? "#ef4444"
                  : "#fbbf24",
              animation:
                serverStatus === "live"
                  ? "pulse 1s infinite"
                  : serverStatus === "checking"
                  ? "blink 1s infinite"
                  : "none",
              fontSize: 18,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color:
                serverStatus === "live"
                  ? GREEN
                  : serverStatus === "down"
                  ? "#ef4444"
                  : "#fbbf24",
              fontWeight: 600,
              fontFamily: '"Noto Sans", sans-serif',
            }}
          >
            {serverStatus === "live"
              ? "Server Live"
              : serverStatus === "down"
              ? "Server Down"
              : "Checking server..."}
          </Typography>
        </Box>
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
            @keyframes blink {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.1; }
            }
          `}
        </style>
        {/* Header */}
        <Paper
          elevation={8}
          sx={{
            p: isMobile ? 2 : isTablet ? 3 : 4,
            mb: 4,
            bgcolor: GREEN,
            color: WHITE,
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
              flexDirection: isMobile ? "column" : "row",
              textAlign: isMobile ? "center" : "left",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: isMobile ? 48 : isTablet ? 56 : 64,
                height: isMobile ? 48 : isTablet ? 56 : 64,
                backdropFilter: "blur(10px)",
              }}
            >
              <FlashOnIcon
                sx={{ fontSize: isMobile ? 24 : isTablet ? 32 : 36 }}
              />
            </Avatar>
            <Box>
              <Typography
                variant={isMobile ? "h5" : isTablet ? "h4" : "h3"}
                component="h1"
                fontWeight="800"
                sx={{ mb: 1, fontFamily: '"Noto Sans", sans-serif', color: WHITE }}
              >
                WattWise Calculator
              </Typography>
              <Typography
                variant={isMobile ? "body2" : isTablet ? "body1" : "h6"}
                sx={{
                  opacity: 0.9,
                  fontWeight: 400,
                  fontFamily: '"Noto Sans", sans-serif',
                  color: "#e0e8f8",
                }}
              >
                Find Your Perfect Power Solution in 4 Simple Steps
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: isMobile ? 1 : isTablet ? 2 : 4,
              mt: 3,
              position: "relative",
              zIndex: 1,
              flexWrap: "wrap",
            }}
          >
            {[
              { icon: <StarIcon />, text: "10+  Curated Inverters" },
              { icon: <CheckCircleIcon />, text: "99% Satisfaction Rate" },
              { icon: <FlashOnIcon />, text: "Instant Recommendations" },
            ].map((stat, i) => (
              <Chip
                key={i}
                icon={stat.icon}
                label={stat.text}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: WHITE,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${GREEN}`,
                  fontWeight: 600,
                  fontFamily: '"Noto Sans", sans-serif',
                  "& .MuiChip-icon": { color: WHITE },
                  mb: isMobile ? 1 : 0,
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Progress Bar */}
        <Card
          elevation={3}
          sx={{
            mb: 4,
            borderRadius: 3,
            width: "100%",
          }}
        >
          <CardContent sx={{ p: isMobile ? 2 : isTablet ? 2.5 : 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant={isMobile ? "subtitle2" : isTablet ? "subtitle1" : "h6"}
                fontWeight="700"
                sx={{ color: GREEN, fontFamily: '"Noto Sans", sans-serif' }}
              >
                Progress: Step {activeStep + 1} of {steps.length}
              </Typography>
              <Chip
                label={`${Math.round(progress)}% Complete`}
                sx={{
                  bgcolor: GREEN,
                  color: WHITE,
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                }}
                size="small"
                variant="filled"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 12,
                borderRadius: 6,
                backgroundColor: `${GREEN}22`,
                "& .MuiLinearProgress-bar": {
                  bgcolor: GREEN,
                  borderRadius: 6,
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Main Content */}
        {!showRecommendation ? (
          <Card
            elevation={6}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              width: "100%",
            }}
          >
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: GREEN }}>
                  {getStepIcon(activeStep)}
                </Avatar>
              }
              title={
                <Typography
                  variant={isMobile ? "subtitle1" : isTablet ? "h6" : "h5"}
                  fontWeight="700"
                  sx={{
                    color: GREEN,
                    fontFamily: '"Noto Sans", sans-serif',
                  }}
                >
                  {steps[activeStep]}
                </Typography>
              }
              sx={{
                bgcolor: "#f0fdf4",
                borderBottom: `3px solid ${GREEN}`,
              }}
            />

            <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
              {/* Step 0: User Info & Appliances */}
              {activeStep === 0 && (
                <Stack spacing={3}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: isMobile ? 2 : isTablet ? 2.5 : 3,
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <Typography
                      variant={
                        isMobile ? "subtitle1" : isTablet ? "subtitle1" : "h6"
                      }
                      fontWeight="600"
                      sx={{
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "#047857",
                        fontFamily: '"Noto Sans", sans-serif',
                      }}
                    >
                      <PersonIcon />
                      Personal Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Full Name"
                          value={user.name}
                          onChange={(e) => {
                            const name = e.target.value;
                            if (/^[A-Za-z\s]*$/.test(name)) {
                              setUser({ ...user, name });
                            }
                          }}
                          fullWidth
                          variant="outlined"
                          placeholder="Enter your full name"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": { borderColor: "#047857" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#047857",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#047857",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <PersonIcon
                                sx={{ mr: 1, color: "action.active" }}
                              />
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Mobile Number"
                          value={user.phone}
                          onChange={(e) => {
                            const formattedPhone = formatPhoneNumber(
                              e.target.value
                            );
                            setUser({ ...user, phone: formattedPhone });
                          }}
                          fullWidth
                          variant="outlined"
                          placeholder="Enter 10-digit mobile number"
                          helperText={
                            user.phone && !validatePhone(user.phone)
                              ? "Please enter a valid 10-digit mobile number"
                              : ""
                          }
                          error={user.phone && !validatePhone(user.phone)}
                          inputProps={{ maxLength: 10, pattern: "[0-9]*" }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": { borderColor: "#047857" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#047857",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#047857",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mr: 1 }}
                              >
                                <PhoneIcon sx={{ color: "action.active" }} />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  +91
                                </Typography>
                              </Stack>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                    {user.phone && validatePhone(user.phone) && (
                      <Alert
                        severity="info"
                        icon={<WhatsAppIcon />}
                        sx={{
                          mt: 2,
                          bgcolor: "#dcfce7",
                          border: "1px solid #047857",
                          "& .MuiAlert-icon": { color: "#25d366" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          sx={{ fontFamily: '"Noto Sans", sans-serif' }}
                        >
                          We'll contact you through WhatsApp for personalized
                          assistance and quick support!
                        </Typography>
                      </Alert>
                    )}
                  </Paper>

                  <Paper
                    elevation={2}
                    sx={{
                      p: isMobile ? 2 : isTablet ? 2.5 : 3,
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <Typography
                      variant={
                        isMobile ? "subtitle1" : isTablet ? "subtitle1" : "h6"
                      }
                      fontWeight="600"
                      sx={{
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "#047857",
                        fontFamily: '"Noto Sans", sans-serif',
                      }}
                    >
                      <FlashOnIcon />
                      Select Your Appliances
                    </Typography>

                    <Grid container spacing={isMobile ? 1 : 2}>
                      {applianceOptions.map((appliance, i) => (
                        <Grid item xs={6} sm={4} md={3} key={i}>
                          <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                              p: isMobile ? 1.5 : isTablet ? 2 : 2.5,
                              height: isMobile ? 100 : isTablet ? 110 : 120,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              borderRadius: 3,
                              border: "2px solid",
                              borderColor: "divider",
                              bgcolor: "white",
                              // Remove animation/transition and transform
                              "&:hover": {
                                boxShadow: `0 8px 25px ${appliance.color}30`,
                                borderColor: appliance.color,
                                bgcolor: `${appliance.color}08`,
                              },
                            }}
                            onClick={() =>
                              handleAddAppliance(
                                appliance.name,
                                appliance.watt,
                                appliance.icon,
                                appliance.color
                              )
                            }
                          >
                            <Avatar
                              sx={{
                                bgcolor: `${appliance.color}20`,
                                color: appliance.color,
                                mb: 1,
                                width: isMobile ? 32 : 40,
                                height: isMobile ? 32 : 40,
                              }}
                            >
                              {appliance.icon}
                            </Avatar>
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color="text.primary"
                              sx={{ fontFamily: '"Noto Sans", sans-serif' }}
                            >
                              {appliance.name}
                            </Typography>
                            <Chip
                              label={`${appliance.watt}W`}
                              size="small"
                              sx={{
                                bgcolor: appliance.color,
                                color: "white",
                                fontWeight: 600,
                                fontSize: isMobile ? "0.7rem" : "0.8rem",
                              }}
                            />
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>

                  {appliances.length > 0 && (
                    <Paper
                      elevation={3}
                      sx={{
                        p: isMobile ? 2 : isTablet ? 2.5 : 3,
                        borderRadius: 2,
                        bgcolor: "white",
                      }}
                    >
                      <Typography
                        variant={
                          isMobile ? "subtitle1" : isTablet ? "subtitle1" : "h6"
                        }
                        fontWeight="600"
                        sx={{
                          mb: 2,
                          color: "#047857",
                          fontFamily: '"Noto Sans", sans-serif',
                        }}
                      >
                        Selected Appliances ({appliances.length})
                      </Typography>
                      <Grid container spacing={1}>
                        {appliances.map((a, i) => (
                          <Grid item xs={12} key={i}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderColor: a.color,
                                bgcolor: `${a.color}08`,
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 1,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: a.color,
                                  width: 32,
                                  height: 32,
                                }}
                              >
                                {a.icon}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight="600"
                                  sx={{ fontFamily: '"Noto Sans", sans-serif' }}
                                >
                                  {a.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontFamily: '"Noto Sans", sans-serif' }}
                                >
                                  {a.watt}W
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveAppliance(i)}
                                sx={{
                                  bgcolor: "error.main",
                                  color: "white",
                                  "&:hover": { bgcolor: "error.dark" },
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                      <Alert
                        severity="success"
                        sx={{
                          mt: 3,
                          bgcolor: "#dcfce7",
                          borderColor: "#047857",
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight="600"
                          sx={{
                            color: "#047857",
                            fontFamily: '"Noto Sans", sans-serif',
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                          }}
                        >
                          Total Power Load: {totalLoad}W
                        </Typography>
                      </Alert>
                    </Paper>
                  )}
                </Stack>
              )}

              {/* Step 1: Backup Time */}
              {activeStep === 1 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    alignItems: "center",
                    py: isMobile ? 2 : isTablet ? 3 : 4,
                    width: "100%",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "#047857",
                      width: isMobile ? 56 : isTablet ? 64 : 80,
                      height: isMobile ? 56 : isTablet ? 64 : 80,
                    }}
                  >
                    <AccessTimeIcon
                      sx={{ fontSize: isMobile ? 28 : isTablet ? 36 : 40 }}
                    />
                  </Avatar>

                  <Box textAlign="center">
                    <Typography
                      variant={isMobile ? "h6" : isTablet ? "h5" : "h4"}
                      fontWeight="700"
                      sx={{
                        mb: 2,
                        color: "#047857",
                        fontFamily: '"Noto Sans", sans-serif',
                      }}
                    >
                      Backup Duration
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 500,
                        fontFamily: '"Noto Sans", sans-serif',
                        fontSize: isMobile ? "0.8rem" : "0.9rem",
                      }}
                    >
                      Consider your typical power outage duration and how long
                      you need your appliances to run
                    </Typography>
                  </Box>
                  <Paper
                    elevation={3}
                    sx={{
                      p: isMobile ? 2 : isTablet ? 3 : 4,
                      borderRadius: 3,
                      width: "100%",
                    }}
                  >
                    <TextField
                      label="Backup Time (hours)"
                      type="number"
                      value={backupTime}
                      onChange={(e) => setBackupTime(Number(e.target.value))}
                      fullWidth
                      size="large"
                      inputProps={{ min: 1, max: 24, step: 0.5 }}
                      helperText="Enter backup time between 1-24 hours"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "1.2rem",
                          "&:hover fieldset": { borderColor: "#047857" },
                          "&.Mui-focused fieldset": { borderColor: "#047857" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#047857",
                        },
                      }}
                    />
                  </Paper>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      mt: 2,
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    {[2, 4, 6, 8].map((hours) => (
                      <Button
                        key={hours}
                        variant={
                          backupTime === hours ? "contained" : "outlined"
                        }
                        onClick={() => setBackupTime(hours)}
                        sx={{
                          borderRadius: 20,
                          minWidth: isMobile ? 60 : isTablet ? 70 : 80,
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          ...(backupTime === hours
                            ? {
                                bgcolor: "#047857",
                                "&:hover": { bgcolor: "#065f46" },
                              }
                            : {
                                borderColor: "#047857",
                                color: "#047857",
                                "&:hover": {
                                  borderColor: "#065f46",
                                  bgcolor: "#f0fdf4",
                                },
                              }),
                        }}
                      >
                        {hours}h
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Step 2: Budget */}
              {activeStep === 2 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    alignItems: "center",
                    py: isMobile ? 2 : isTablet ? 3 : 4,
                    width: "100%",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "#047857",
                      width: isMobile ? 56 : isTablet ? 64 : 80,
                      height: isMobile ? 56 : isTablet ? 64 : 80,
                    }}
                  >
                    <AccountBalanceWalletIcon
                      sx={{ fontSize: isMobile ? 28 : isTablet ? 36 : 40 }}
                    />
                  </Avatar>

                  <Box textAlign="center">
                    <Typography
                      variant={isMobile ? "h6" : isTablet ? "h5" : "h4"}
                      fontWeight="700"
                      sx={{
                        mb: 2,
                        color: "#047857",
                        fontFamily: '"Noto Sans", sans-serif',
                      }}
                    >
                      Investment Range
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 500,
                        fontFamily: '"Noto Sans", sans-serif',
                        fontSize: isMobile ? "0.8rem" : "0.9rem",
                      }}
                    >
                      Set your budget to get recommendations within your price
                      range
                    </Typography>
                  </Box>
                  <Paper
                    elevation={3}
                    sx={{
                      p: isMobile ? 2 : isTablet ? 3 : 4,
                      borderRadius: 3,
                      width: "100%",
                    }}
                  >
                    <TextField
                      label="Budget (₹)"
                      type="number"
                      value={budget}
                      onChange={(e) => setBudget(Number(e.target.value))}
                      fullWidth
                      size="large"
                      inputProps={{ min: 5000, step: 1000 }}
                      helperText="Minimum budget: ₹5,000"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          fontSize: "1.2rem",
                          "&:hover fieldset": { borderColor: "#047857" },
                          "&.Mui-focused fieldset": { borderColor: "#047857" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#047857",
                        },
                      }}
                    />
                  </Paper>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    justifyContent="center"
                  >
                    {[10000, 15000, 25000, 35000, 50000].map((amount) => (
                      <Button
                        key={amount}
                        variant={budget === amount ? "contained" : "outlined"}
                        onClick={() => setBudget(amount)}
                        sx={{
                          borderRadius: 20,
                          mb: 1,
                          minWidth: isMobile ? 100 : isTablet ? 110 : 120,
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          ...(budget === amount
                            ? {
                                bgcolor: "#047857",
                                "&:hover": { bgcolor: "#065f46" },
                              }
                            : {
                                borderColor: "#047857",
                                color: "#047857",
                                "&:hover": {
                                  borderColor: "#065f46",
                                  bgcolor: "#f0fdf4",
                                },
                              }),
                        }}
                      >
                        ₹{amount.toLocaleString()}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Step 3: Inverter Type */}
              {activeStep === 3 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    alignItems: "center",
                    py: isMobile ? 2 : isTablet ? 3 : 4,
                    width: "100%",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "#047857",
                      width: isMobile ? 56 : isTablet ? 64 : 80,
                      height: isMobile ? 56 : isTablet ? 64 : 80,
                    }}
                  >
                    <SettingsIcon
                      sx={{ fontSize: isMobile ? 28 : isTablet ? 36 : 40 }}
                    />
                  </Avatar>

                  <Box textAlign="center">
                    <Typography
                      variant={isMobile ? "h6" : isTablet ? "h5" : "h4"}
                      fontWeight="700"
                      sx={{
                        mb: 2,
                        color: "#047857",
                        fontFamily: '"Noto Sans", sans-serif',
                      }}
                    >
                      Inverter Technology
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 500,
                        fontFamily: '"Noto Sans", sans-serif',
                        fontSize: isMobile ? "0.8rem" : "0.9rem",
                      }}
                    >
                      Choose the inverter type based on your appliance
                      requirements
                    </Typography>
                  </Box>
                  <Grid container spacing={2} sx={{ width: "100%" }}>
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={inverterType === "pure_sine" ? 6 : 2}
                        sx={{
                          p: isMobile ? 2 : isTablet ? 2.5 : 3,
                          borderRadius: 3,
                          cursor: "pointer",
                          border: inverterType === "pure_sine" ? 3 : 1,
                          borderColor:
                            inverterType === "pure_sine"
                              ? GREEN
                              : "divider",
                          bgcolor:
                            inverterType === "pure_sine" ? "#f0fdf4" : "white",
                          // Remove transform and transition
                        }}
                        onClick={() => setInverterType("pure_sine")}
                      >
                        <Typography
                          variant={
                            isMobile
                              ? "subtitle1"
                              : isTablet
                              ? "subtitle1"
                              : "h6"
                          }
                          fontWeight="700"
                          sx={{
                            mb: 2,
                            color: "#047857",
                            fontFamily: '"Noto Sans", sans-serif',
                          }}
                        >
                          Pure Sine Wave
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            fontFamily: '"Noto Sans", sans-serif',
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                          }}
                        >
                          Best for sensitive electronics like computers, TVs,
                          and modern appliances
                        </Typography>
                        <Chip
                          label="Recommended"
                          sx={{ bgcolor: "#047857", color: "white" }}
                          size="small"
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={inverterType === "square_wave" ? 6 : 2}
                        sx={{
                          p: isMobile ? 2 : isTablet ? 2.5 : 3,
                          borderRadius: 3,
                          cursor: "pointer",
                          border: inverterType === "square_wave" ? 3 : 1,
                          borderColor:
                            inverterType === "square_wave"
                              ? GREEN
                              : "divider",
                          bgcolor:
                            inverterType === "square_wave"
                              ? "#f0fdf4"
                              : "white",
                          // Remove transform and transition
                        }}
                        onClick={() => setInverterType("square_wave")}
                      >
                        <Typography
                          variant={
                            isMobile
                              ? "subtitle1"
                              : isTablet
                              ? "subtitle1"
                              : "h6"
                          }
                          fontWeight="700"
                          sx={{
                            mb: 2,
                            color: "#047857",
                            fontFamily: '"Noto Sans", sans-serif',
                          }}
                        >
                          Square Wave
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            fontFamily: '"Noto Sans", sans-serif',
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                          }}
                        >
                          Budget-friendly option suitable for basic appliances
                          like fans and lights
                        </Typography>
                        <Chip
                          label="Budget Option"
                          sx={{ borderColor: "#047857", color: "#047857" }}
                          variant="outlined"
                          size="small"
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                  <Alert
                    severity="success"
                    sx={{
                      width: "100%",
                      bgcolor: "#dcfce7",
                      borderColor: "#047857",
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: '"Noto Sans", sans-serif' }}
                    >
                      <strong>Recommendation:</strong> Pure sine wave inverters
                      are ideal for modern homes with electronic devices and
                      ensure better appliance longevity.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </CardContent>
            <Divider />

            {/* Navigation */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: isMobile ? "center" : "space-between",
                alignItems: "center",
                gap: isMobile ? 2 : 0,
                p: isMobile ? 2 : isTablet ? 2.5 : 3,
                bgcolor: "#f8fafc",
                width: "100%",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBackIcon />}
                sx={{
                  minWidth: isMobile ? "100%" : isTablet ? 110 : 120,
                  borderRadius: 25,
                  borderWidth: 2,
                  borderColor: GREEN,
                  color: GREEN,
                  fontSize: isMobile ? "0.9rem" : "0.9rem",
                  "&:hover": {
                    borderWidth: 2,
                    borderColor: GREEN,
                    bgcolor: "#f0fdf4",
                  },
                  "&:disabled": {
                    borderColor: "rgba(0, 0, 0, 0.12)",
                    color: "rgba(0, 0, 0, 0.26)",
                  },
                  order: isMobile ? 1 : 0,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Previous
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: isMobile ? "0.9rem" : "0.9rem",
                  my: isMobile ? 1 : 0,
                  order: isMobile ? 0 : 1,
                  width: isMobile ? "100%" : "auto",
                  textAlign: isMobile ? "center" : "center",
                }}
              >
                Step {activeStep + 1} of {steps.length}
              </Typography>

              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={
                  activeStep === steps.length - 1 ? (
                    <CheckCircleIcon />
                  ) : (
                    <ArrowForwardIcon />
                  )
                }
                sx={{
                  minWidth: isMobile ? "100%" : isTablet ? 140 : 160,
                  borderRadius: 25,
                  bgcolor: GREEN,
                  boxShadow: `0 4px 15px ${GREEN}`,
                  fontSize: isMobile ? "0.9rem" : "0.9rem",
                  "&:hover": {
                    bgcolor: GREEN,
                    boxShadow: `0 6px 20px ${GREEN}`,
                  },
                  order: isMobile ? 2 : 2,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                {activeStep === steps.length - 1
                  ? "Get My Recommendation"
                  : "Continue"}
              </Button>
            </Box>
          </Card>
        ) : (
          <Recommendation
            load={totalLoad}
            backupTime={backupTime}
            budget={budget}
            inverterType={inverterType}
          />
        )}
      </Container>
    </Box>
  );
};

export default Inverter;